import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Filter, X, Eye, Trash2, User, KeyRound, Upload } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Pagination from '../components/Pagination';
import AddPatient from '../components/modals/AddPatient';
import BulkUploadPatients from '../components/modals/BulkUploadPatients';
import PasswordUpdateModal from '../components/modals/PasswordUpdateModal';
import { getPatients, deletePatient, getDoctorPatients, updatePatientStatus } from '../redux/patientSlice';
import CommonLoader from '../components/CommonLoader';
import ErrorPage from '../components/ErrorPage';
import { getFileURL } from '../utils/fileURL';
import errorimg from '../assets/img/colorkit (1).svg';

const formatDob = (dob) => {
    if (!dob) return '-';
    let date;
    const str = String(dob).trim();
    if (str.includes('T') || /^\d{4}-\d{2}-\d{2}$/.test(str)) {
        date = new Date(str);
    } else if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(str)) {
        const parts = str.split(/[/-]/);
        date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    } else {
        date = new Date(str);
    }
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
import { getDoctorById } from '../redux/doctorSlice';
import { getCountries } from '../redux/masterSlice';
import { FilterPanel, FilterSection, FilterOptionList, FilterCountryList, FilterPopover } from '../components/common/FilterPanel';
import { SlidersHorizontal } from 'lucide-react';
import MasterLabel from '../components/common/MasterLabel';

export default function Patient() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation(['patientpage']);
    const isInitialCall = useRef(true);

    const {
        docPatients, patients, isLoading, patientsLoading, error,
        total, page, limit, totalPages,
        docTotal, docPage, docLimit, docTotalPages
    } = useSelector((state) => state.patient);
    const { selectedDoctor, loading: doctorLoading } = useSelector((state) => state.doctor);
    const { countries } = useSelector((state) => state.master);
    const userRole = localStorage.getItem('Role');
    const doctorId = userRole === 'doctor' ? localStorage.getItem('ID') : '';
    const language = localStorage.getItem('i18nextLng');

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [selectedPatientId, setSelectedPatientId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [searchError, setSearchError] = useState('');
    const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('patientActiveTab') || 'active'); // 'active' | 'inactive' | 'in-active'
    // Bumped after a patient is created to force a list refetch even when the tab/page don't change
    const [refreshKey, setRefreshKey] = useState(0);

    // Persist active tab to session storage
    useEffect(() => {
        sessionStorage.setItem('patientActiveTab', activeTab);
    }, [activeTab]);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState({
        ageStart: '',
        ageEnd: '',
        countryId: [],
        gender: []
    });
    const [filters, setFilters] = useState({
        ageStart: '',
        ageEnd: '',
        countryId: [],
        gender: []
    });
    const [ageErrors, setAgeErrors] = useState({ ageStart: '', ageEnd: '' });

    const searchTimeoutRef = useRef(null);
    const inputRef = useRef(null);
    const filterButtonRef = useRef(null);
    const filterRef = useRef(null);

    // Validate search input
    const validateSearchInput = (value) => {
        // Remove any potentially dangerous characters
        const sanitized = value.replace(/[<>]/g, '');

        // Check length
        if (sanitized.length > 50) {
            return t('patientpage.search_too_long', 'Search term cannot exceed 50 characters');
        }

        // Check for only whitespace
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('patientpage.search_spaces_only', 'Search term cannot contain only spaces');
        }

        return '';
    };

    // Validate age inputs
    const validateAgeInputs = (ageStart, ageEnd) => {
        const errors = { ageStart: '', ageEnd: '' };

        if (ageStart) {
            const startNum = parseInt(ageStart, 10);
            if (isNaN(startNum) || startNum < 0 || startNum > 120) {
                errors.ageStart = t('patientpage.age_range_error', 'Age must be between 0 and 120');
            } else if (ageEnd) {
                const endNum = parseInt(ageEnd, 10);
                if (!isNaN(endNum) && startNum > endNum) {
                    errors.ageStart = t('patientpage.age_start_greater_than_end', 'Start age cannot be greater than end age');
                }
            }
        }

        if (ageEnd) {
            const endNum = parseInt(ageEnd, 10);
            if (isNaN(endNum) || endNum < 0 || endNum > 120) {
                errors.ageEnd = t('patientpage.age_range_error', 'Age must be between 0 and 120');
            } else if (ageStart) {
                const startNum = parseInt(ageStart, 10);
                if (!isNaN(startNum) && endNum < startNum) {
                    errors.ageEnd = t('patientpage.age_end_less_than_start', 'End age cannot be less than start age');
                }
            }
        }

        setAgeErrors(errors);
        return Object.values(errors).every(error => !error);
    };

    // Debounce search term (only if no error)
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Only debounce if there's no error
        if (!searchError) {
            searchTimeoutRef.current = setTimeout(() => {
                // Trim the search term before setting it
                const trimmedSearch = searchTerm.trim();
                setDebouncedSearchTerm(trimmedSearch);
                setCurrentPage(1);
            }, 500);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, searchError]);

    // Sync tempFilters with filters when popup opens
    useEffect(() => {
        if (isFilterOpen) {
            setTempFilters({ ...filters });
            setAgeErrors({ ageStart: '', ageEnd: '' });
        }
    }, [isFilterOpen, filters]);

    // Close filter on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target) &&
                filterButtonRef.current &&
                !filterButtonRef.current.contains(event.target)
            ) {
                // Reset tempFilters to current filters when closing without applying
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    // Refocus search input after loading
    useEffect(() => {
        const active = document.activeElement === inputRef.current;
        if (!active && inputRef.current && searchTerm.length > 0) {
            inputRef.current.focus();
        }
    }, [isLoading, patientsLoading]);

    // Fetch patients with filters
    useEffect(() => {
        // Don't fetch if there's a search error
        if (searchError) {
            return;
        }

        const payload = {
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchTerm,
            ageStart: filters.ageStart,
            ageEnd: filters.ageEnd,
        };
        if (filters.gender.length > 0) payload.gender = filters.gender.join(',');
        if (filters.countryId.length > 0) payload.countryId = filters.countryId.join(',');

        if (activeTab === 'in-active') {
            // For Disabled tab: use status=inactive
            payload.status = 'inactive';
        } else if (activeTab === 'active') {
            // For Active tab: use profileStatus=complete (existing behavior)
            payload.profileStatus = 'complete';
        } else if (activeTab === 'inactive') {
            // For Pending tab: use profileStatus=incomplete (existing behavior)
            payload.profileStatus = 'incomplete';
        }

        if (userRole === 'admin') {
            dispatch(getPatients(payload));
        } else if (userRole === 'doctor') {
            dispatch(getDoctorPatients({ doctorId, ...payload }));
        }
    }, [currentPage, itemsPerPage, debouncedSearchTerm, activeTab, filters, dispatch, userRole, doctorId, searchError, refreshKey]);

    // Fetch doctor profile on mount (for doctor role)
    useEffect(() => {
        if (userRole === 'doctor' && doctorId) {
            dispatch(getDoctorById(doctorId));
        }
    }, [dispatch, userRole, doctorId]);

    useEffect(() => {
        dispatch(getCountries({ status: 'active', limit: 100 }));
    }, [dispatch]);

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);
    const handleAddPatient = () => {
        handleCloseModal();
        // New patients are created with profileStatus 'complete', so they belong in the Active tab.
        if (activeTab !== 'active') {
            setActiveTab('active');
        }
        setCurrentPage(1);
        // Force a refetch so the newly created patient shows up instantly, even when
        // we're already on the Active tab / page 1 (where no state change would otherwise occur).
        setRefreshKey((k) => k + 1);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > 50
            ? sanitized.substring(0, 50)
            : sanitized;

        // Always update searchTerm so the input stays in sync
        setSearchTerm(finalValue);

        // Validate input
        const error = validateSearchInput(finalValue);
        setSearchError(error);
        setCurrentPage(1);
    };

    const handleStatusToggle = async (patient) => {
        const currentStatus = patient.status === 'active';
        // If status is inactive, send true; else active, send false
        const newStatus = !currentStatus;
        const actionText = newStatus ? t('patientpage.activate') || 'activate' : t('patientpage.deactivate') || 'deactivate';

        // Show confirmation dialog
        const result = await Swal.fire({
            title: t('patientpage.status_toggle_confirm_title') || 'Confirm Status Change',
            text: t('patientpage.status_toggle_confirm_text') || `Are you sure you want to ${actionText} this patient?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('patientpage.confirm') || 'Confirm',
            cancelButtonText: t('patientpage.cancel') || 'Cancel',
        });

        if (!result.isConfirmed) {
            return; // User cancelled
        }

        try {
            await dispatch(updatePatientStatus({
                patientId: patient.patientId || patient.id,
                status: newStatus
            })).unwrap();

            // Refresh the patients list based on current tab
            const payload = {
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearchTerm,
                ageStart: filters.ageStart,
                ageEnd: filters.ageEnd,
            };
            if (filters.gender.length > 0) payload.gender = filters.gender.join(',');
            if (filters.countryId.length > 0) payload.countryId = filters.countryId.join(',');

            if (activeTab === 'in-active') {
                payload.status = 'inactive';
            } else if (activeTab === 'active') {
                payload.profileStatus = 'complete';
            } else if (activeTab === 'inactive') {
                payload.profileStatus = 'incomplete';
            }

            if (userRole === 'admin') {
                dispatch(getPatients(payload));
            } else if (userRole === 'doctor') {
                dispatch(getDoctorPatients({ doctorId, ...payload }));
            }

            // Show success message
            toast.success(newStatus
                    ? (t('patientpage.status_activated') || 'Patient activated successfully')
                    : (t('patientpage.status_deactivated') || 'Patient deactivated successfully'));
        } catch (error) {
            toast.warn(language === 'en'
                    ? error?.error?.response?.data?.message?.en || error?.error?.message || t('patientpage.status_update_error_text') || 'Failed to update patient status'
                    : language === 'fr'
                        ? error?.error?.response?.data?.message?.fr || error?.error?.message || t('patientpage.status_update_error_text') || 'Échec de la mise à jour du statut du patient'
                        : t('patientpage.status_update_error_text') || 'Failed to update patient status');
        }
    };

    const handleDeletePatient = async (patientId, patientName) => {
        const result = await Swal.fire({
            title: t('patientpage.delete_confirm_title'),
            text: t('patientpage.delete_confirm_text', { patientName }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('patientpage.delete_confirm_button'),
            cancelButtonText: t('patientpage.cancel'),
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    const response = await dispatch(deletePatient(patientId)).unwrap();
                    return response;
                } catch (error) {
                    toast.warn(language === 'en'
                            ? error?.response?.data?.message?.en
                            : language === 'fr'
                                ? error?.response?.data?.message?.fr
                                : t('patientpage.delete_error_text'));
                    throw error;
                }
            },
            allowOutsideClick: () => !Swal.isLoading(),
        });

        if (result.isConfirmed) {
            toast.success(t('patientpage.delete_success_text'));

            const payload = {
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearchTerm,
                ...filters
            };

            if (activeTab === 'in-active') {
                payload.status = 'inactive';
            } else if (activeTab === 'active') {
                payload.profileStatus = 'complete';
            } else if (activeTab === 'inactive') {
                payload.profileStatus = 'incomplete';
            }

            if (userRole === 'admin') {
                dispatch(getPatients(payload));
            } else if (userRole === 'doctor') {
                dispatch(getDoctorPatients({ doctorId, ...payload }));
            }
        }
    };

    // Format patients
    const rawPatients = (userRole === 'doctor' ? docPatients : patients) || [];
    const formattedPatients = rawPatients.map((patient, index) => ({
        id: patient.patientId || index + 1,
        patientId: patient.patientId,
        name: `${patient.name || ''} ${patient.surname || ''}`.trim(),
        firstName: patient.name || '',
        surname: patient.surname || '',
        image: patient.image,
        email: patient.email,
        phone: patient.phone,
        gender: patient.gender || '-',
        dob: formatDob(patient.dob),
        profile: patient.profile,
        address: patient.address,
        city: patient.city,
        zipCode: patient.zipCode,
        profileStatus: patient.profileStatus,
        status: patient.status || 'inactive',
    }));

    const currentPatients = formattedPatients;
    const totalItems = userRole === 'doctor' ? docTotal : total;
    const indexOfFirstPatient = (currentPage - 1) * itemsPerPage;

    // Error handling
    if (error?.status >= 500 && error?.status <= 599) {
        return <ErrorPage error={error} pageName={t('patientpage.page_name')} />;
    }

    if (userRole === 'doctor' && (!selectedDoctor?.availability || selectedDoctor.availability.length === 0) && !doctorLoading) {
        return (
            <div className="h-full bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex items-center justify-center">
                <div className="text-center max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
                    <img
                        src={errorimg}
                        alt={t('patientpage.error_alt')}
                        className="mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 object-contain"
                    />
                    <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#68c0c0] mb-3 sm:mb-4">
                        {t('patientpage.unable_to_access')}
                    </h2>
                    <p className="text-sm sm:text-base md:text-lg text-gray-800 mb-2 sm:mb-3 md:mb-4">
                        {t('patientpage.no_patient_access')}
                    </p>
                    <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-6 md:mb-8">
                        {t('patientpage.update_profile_message')}
                    </p>
                    <button
                        data-testid="patient-go-to-profile-button"
                        onClick={() => navigate('/doctorProfile')}
                        className="px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 cursor-pointer bg-[#4ABDBD] text-white font-semibold rounded-lg shadow-sm text-xs sm:text-sm md:text-base transition-all hover:bg-[#3aa8a8] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50"
                    >
                        {t('patientpage.go_to_profile')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="patient-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            {/* Header Row */}
            <div data-testid="patient-page-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h1 data-testid="patient-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('patientpage.heading')}
                </h1>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                    <button
                        data-testid="bulk-upload-patient-button"
                        onClick={() => setIsBulkModalOpen(true)}
                        disabled={isLoading || patientsLoading}
                        className={`group flex items-center justify-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 border border-[#4ABDBD] text-[#3AA8A8] rounded-xl shadow-sm hover:bg-[#ecfdfd] transition-all duration-300 font-semibold text-sm ${(isLoading || patientsLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <Upload className="w-5 h-5" />
                        <span>{t('patientpage.bulk_upload', 'Bulk Upload')}</span>
                    </button>
                    <button
                        data-testid="add-patient-button"
                        onClick={handleOpenModal}
                        disabled={isLoading || patientsLoading}
                        className={`group flex items-center justify-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm ${(isLoading || patientsLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <Plus data-testid="add-patient-icon" className="w-5 h-5" />
                        <span data-testid="add-patient-text">{t('patientpage.add_patient')}</span>
                    </button>
                </div>
            </div>

            {/* Search + Filter Row */}
            <div data-testid="search-container" className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
                <div className="flex gap-2 bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 relative">
                    <button
                        data-testid="patient-tab-inactive"
                        onClick={() => { setActiveTab('inactive'); setCurrentPage(1); }}
                        className={`relative px-6 py-2 font-medium text-sm rounded-xl transition-all duration-300 z-10 ${activeTab === 'inactive'
                            ? 'text-white'
                            : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                            }`}
                    >
                        {activeTab === 'inactive' && (
                            <motion.div
                                layoutId="activePatientStatusTab"
                                className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                            />
                        )}
                        <span className="relative z-20">{t('patientpage.inactive')}</span>
                    </button>

                    <button
                        data-testid="patient-tab-active"
                        onClick={() => { setActiveTab('active'); setCurrentPage(1); }}
                        className={`relative px-6 py-2 font-medium text-sm rounded-xl transition-all duration-300 z-10 ${activeTab === 'active'
                            ? 'text-white'
                            : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                            }`}
                    >
                        {activeTab === 'active' && (
                            <motion.div
                                layoutId="activePatientStatusTab"
                                className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                            />
                        )}
                        <span className="relative z-20">{t('patientpage.active')}</span>
                    </button>

                    <button
                        data-testid="patient-tab-in-active"
                        onClick={() => { setActiveTab('in-active'); setCurrentPage(1); }}
                        className={`relative px-6 py-2 font-medium text-sm rounded-xl transition-all duration-300 z-10 ${activeTab === 'in-active'
                            ? 'text-white'
                            : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                            }`}
                    >
                        {activeTab === 'in-active' && (
                            <motion.div
                                layoutId="activePatientStatusTab"
                                className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                            />
                        )}
                        <span className="relative z-20">{t('patientpage.disabled') || 'Disabled'}</span>
                    </button>
                </div>
                <div className="relative w-full sm:w-80">
                    <input
                        ref={inputRef}
                        data-testid="search-input"
                        type="text"
                        placeholder={t('patientpage.search_placeholder')}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        disabled={isLoading || patientsLoading}
                        maxLength={50}
                        className={`w-full p-2 pl-10 pr-8 border rounded-xl focus:outline-none focus:ring-2 ${searchError
                            ? 'border-red-500 focus:ring-red-500 bg-red-50'
                            : 'border-gray-300 focus:ring-[#4ABDBD]'
                            } ${(isLoading || patientsLoading) ? "opacity-70" : ""}`}
                    />
                    <Search data-testid="search-icon" className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#4ABDBD]" />
                    {searchError && (
                        <p data-testid="patient-search-error" className="text-red-600 text-xs mt-1 absolute -bottom-5 left-0">{searchError}</p>
                    )}
                </div>

                {/* Filter Button + Popup */}
                <div data-testid="filter-container" className="relative w-full sm:w-auto">
                    <button
                        data-testid="patient-filter-button"
                        ref={filterButtonRef}
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        className="group flex items-center justify-center gap-2  px-3 py-2.5 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                        aria-label={t('patientpage.filter')}
                        aria-expanded={isFilterOpen}
                    >
                        <SlidersHorizontal className="w-5 h-5" />
                        {/* {t('patientpage.filter')} */}
                    </button>

                    {isFilterOpen && (
                        <FilterPopover ref={filterRef}>
                            <FilterPanel
                                onClear={() => {
                                    setTempFilters({ ageStart: '', ageEnd: '', countryId: [], gender: [] });
                                    setFilters({ ageStart: '', ageEnd: '', countryId: [], gender: [] });
                                    setAgeErrors({ ageStart: '', ageEnd: '' });
                                    setCurrentPage(1);
                                    setIsFilterOpen(false);
                                }}
                                onApply={() => {
                                    if (validateAgeInputs(tempFilters.ageStart, tempFilters.ageEnd)) {
                                        setFilters(tempFilters);
                                        setCurrentPage(1);
                                        setIsFilterOpen(false);
                                    }
                                }}
                                clearLabel={t('patientpage.clear')}
                                applyLabel={t('patientpage.apply')}
                            >
                                <FilterSection title={t('patientpage.age_range')} isActive={!!(tempFilters.ageStart || tempFilters.ageEnd)}>
                                    <div className="flex gap-2">
                                        <div className="w-full">
                                            <input
                                                data-testid="patient-filter-age-start-input"
                                                type="number"
                                                placeholder={t('patientpage.min_age')}
                                                value={tempFilters.ageStart}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setTempFilters({ ...tempFilters, ageStart: value });
                                                    validateAgeInputs(value, tempFilters.ageEnd);
                                                }}
                                                className={`w-full p-2 border rounded-xl text-sm focus:outline-none focus:ring-2 ${ageErrors.ageStart
                                                    ? 'border-red-500 focus:ring-red-500 bg-red-50'
                                                    : 'border-gray-300 focus:ring-[#4ABDBD]'
                                                    }`}
                                                min="0"
                                                max="120"
                                            />
                                            {ageErrors.ageStart && (
                                                <p data-testid="patient-filter-age-start-error" className="text-red-600 text-xs mt-1">{ageErrors.ageStart}</p>
                                            )}
                                        </div>
                                        <div className="w-full">
                                            <input
                                                data-testid="patient-filter-age-end-input"
                                                type="number"
                                                placeholder={t('patientpage.max_age')}
                                                value={tempFilters.ageEnd}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setTempFilters({ ...tempFilters, ageEnd: value });
                                                    validateAgeInputs(tempFilters.ageStart, value);
                                                }}
                                                className={`w-full p-2 border rounded-xl text-sm focus:outline-none focus:ring-2 ${ageErrors.ageEnd
                                                    ? 'border-red-500 focus:ring-red-500 bg-red-50'
                                                    : 'border-gray-300 focus:ring-[#4ABDBD]'
                                                    }`}
                                                min="0"
                                                max="120"
                                            />
                                            {ageErrors.ageEnd && (
                                                <p data-testid="patient-filter-age-end-error" className="text-red-600 text-xs mt-1">{ageErrors.ageEnd}</p>
                                            )}
                                        </div>
                                    </div>
                                </FilterSection>

                                <FilterSection title={t('patientpage.country')} isActive={tempFilters.countryId.length > 0}>
                                    <FilterCountryList
                                        multiple
                                        value={tempFilters.countryId}
                                        onChange={(val) => setTempFilters({ ...tempFilters, countryId: val })}
                                        countries={countries}
                                        searchPlaceholder={t('patientpage.search_placeholder', 'Search...')}
                                    />
                                </FilterSection>

                                <FilterSection title={t('patientpage.gender')} isActive={tempFilters.gender.length > 0}>
                                    <FilterOptionList
                                        multiple
                                        value={tempFilters.gender}
                                        onChange={(val) => setTempFilters({ ...tempFilters, gender: val })}
                                        options={[
                                            { value: 'male', label: t('patientpage.male') },
                                            { value: 'female', label: t('patientpage.female') },
                                            { value: 'Not_Communicated', label: t('common.gender.notCommunicated', 'Not Communicated') },
                                            { value: 'other', label: t('patientpage.other') },
                                        ]}
                                    />
                                </FilterSection>
                            </FilterPanel>
                        </FilterPopover>
                    )}
                </div>
            </div>

            {/* ---- Applied Filters Display ---- */}
            {(filters.ageStart || filters.ageEnd || filters.countryId.length > 0 || filters.gender.length > 0) && (
                <div className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700 mr-2">
                            {t('patientpage.applied_filters', 'Applied Filters')}:
                        </span>

                        {filters.ageStart && (
                            <div data-testid="patient-applied-filter-age-start" className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('patientpage.min_age', 'Min Age')}:</span> {filters.ageStart}
                                </span>
                                <button
                                    data-testid="patient-applied-filter-age-start-remove"
                                    onClick={() => setFilters({ ...filters, ageStart: '' })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        )}

                        {filters.ageEnd && (
                            <div data-testid="patient-applied-filter-age-end" className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('patientpage.max_age', 'Max Age')}:</span> {filters.ageEnd}
                                </span>
                                <button
                                    data-testid="patient-applied-filter-age-end-remove"
                                    onClick={() => setFilters({ ...filters, ageEnd: '' })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        )}

                        {filters.countryId.map((cid) => (
                            <div key={`country-${cid}`} data-testid={`patient-applied-filter-country-${cid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('patientpage.country')}:</span> <MasterLabel type="masterCountry" id={cid} field="countryName" fallback={countries?.find(c => c.countryId === cid)?.countryName || cid} />
                                </span>
                                <button
                                    data-testid={`patient-applied-filter-country-remove-${cid}`}
                                    onClick={() => setFilters({ ...filters, countryId: filters.countryId.filter((x) => x !== cid) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}

                        {filters.gender.map((g) => (
                            <div key={`gender-${g}`} data-testid={`patient-applied-filter-gender-${g}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('patientpage.gender')}:</span> {t(`patientpage.${g}`)}
                                </span>
                                <button
                                    data-testid={`patient-applied-filter-gender-remove-${g}`}
                                    onClick={() => setFilters({ ...filters, gender: filters.gender.filter((x) => x !== g) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}

                        <button
                            data-testid="patient-clear-all-filters-button"
                            onClick={() => {
                                setFilters({ ageStart: '', ageEnd: '', countryId: [], gender: [] });
                                setTempFilters({ ageStart: '', ageEnd: '', countryId: [], gender: [] });
                                setAgeErrors({ ageStart: '', ageEnd: '' });
                                setCurrentPage(1);
                            }}
                            className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
                        >
                            <X className="h-3.5 w-3.5" />
                            {t('patientpage.clear_all', 'Clear All')}
                        </button>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {(isLoading || patientsLoading) ? (
                <div data-testid="patient-loading-state" className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span data-testid="patient-loading-text" className="text-xl text-[#4ABDBD]">{t('patientpage.loading')}</span>
                </div>
            ) : (
                <>
                    {/* Desktop Table */}
                    <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="patients-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.serial_no')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.full_name')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.email')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.phone')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.dob')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.gender')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.status_label')}</th>
                                    {activeTab !== 'active' && (<th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.status')}</th>)}
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('patientpage.action')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentPatients.length > 0 ? (
                                    currentPatients.map((patient, index) => {
                                        // Generate initials from name and surname
                                        const getInitials = () => {
                                            const nameInitial = patient.firstName ? patient.firstName.charAt(0).toUpperCase() : '';
                                            const surnameInitial = patient.surname ? patient.surname.charAt(0).toUpperCase() : '';
                                            return nameInitial + surnameInitial;
                                        };
                                        const initials = getInitials();
                                        return (
                                            <tr key={patient.id} data-testid={`patient-row-${patient.patientId}`} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group">
                                                <td data-testid={`patient-serial-${patient.patientId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">{indexOfFirstPatient + index + 1}</td>
                                                <td data-testid={`patient-name-cell-${patient.patientId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">
                                                    <div className="flex items-center gap-3">
                                                        <div className="relative w-10 h-10 flex-shrink-0">
                                                            {patient.image ? (
                                                                <img
                                                                    data-testid={`patient-image-${patient.patientId}`}
                                                                    src={getFileURL(patient.image)}
                                                                    alt={patient.name || 'Patient'}
                                                                    className="w-10 h-10 rounded-full object-cover shadow-md"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        const fallback = e.target.parentElement.querySelector('.image-fallback');
                                                                        if (fallback) fallback.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div
                                                                className={`image-fallback ${patient.image ? 'hidden' : 'flex'} absolute inset-0 w-10 h-10 rounded-full bg-[#4ABDBD] items-center justify-center shadow-md`}
                                                            >
                                                                {initials ? (
                                                                    <span className="text-white text-sm font-bold uppercase">
                                                                        {initials}
                                                                    </span>
                                                                ) : (
                                                                    <User className="w-5 h-5 text-white" />
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span data-testid={`patient-name-${patient.patientId}`}>{patient.name || '-'}</span>
                                                    </div>
                                                </td>
                                                <td data-testid={`patient-email-${patient.patientId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">{patient.email}</td>
                                                <td data-testid={`patient-phone-${patient.patientId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">{patient.phone ? patient.phone : '-'}</td>
                                                <td data-testid={`patient-dob-${patient.patientId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">{formatDob(patient.dob)}</td>
                                                <td className="p-3 text-xs">
                                                    <span data-testid={`patient-gender-${patient.patientId}`} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 text-[#3AA8A8] border border-[#4ABDBD]/20">
                                                        {t(`patientpage.${patient.gender}`)}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-xs">
                                                    <div className="relative">
                                                        <div data-testid={`patient-status-toggle-${patient.patientId}`} className={`relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full transition-colors duration-300 ${patient.status === 'active'
                                                            ? 'bg-[#4ABDBD]'
                                                            : 'bg-gray-300'
                                                            } ${(isLoading || patientsLoading) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                            onClick={() => !(isLoading || patientsLoading) && handleStatusToggle(patient)}
                                                        >
                                                            <span className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${patient.status === 'active'
                                                                ? 'translate-x-[24px] sm:translate-x-[24px]'
                                                                : 'translate-x-1'
                                                                }`}></span>
                                                        </div>
                                                    </div>
                                                </td>
                                                {activeTab !== 'active' && (<td className="p-3 text-xs ">
                                                    {patient.profileStatus ? (
                                                        <span data-testid={`patient-profile-status-completed-${patient.patientId}`} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-green-500/10 to-green-300/10 text-green-700 border border-green-500/20">
                                                            {t('patientpage.completed')}
                                                        </span>
                                                    ) : (
                                                        <span data-testid={`patient-profile-status-incomplete-${patient.patientId}`} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-red-500/10 to-red-300/10 text-red-700 border border-red-500/20">
                                                            {t(`patientpage.profile`)}
                                                        </span>
                                                    )}
                                                </td>)}
                                                <td className="p-3 text-xs flex justify-center gap-2">
                                                    <button
                                                        data-testid={`patient-view-button-${patient.patientId}`}
                                                        onClick={() => navigate('/patientProfile', { state: { patientId: patient.id, returnTab: activeTab } })}
                                                        className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                    >
                                                        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                        <Eye className="h-4 w-4 relative z-10" />
                                                    </button>
                                                    {userRole === 'admin' && (
                                                        <button
                                                            data-testid={`patient-password-button-${patient.patientId}`}
                                                            onClick={() => {
                                                                setSelectedPatientId(patient.id);
                                                                setIsPasswordModalOpen(true);
                                                            }}
                                                            className="group/btn text-purple-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                        >
                                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                            <KeyRound className="h-4 w-4 relative z-10" />
                                                        </button>
                                                    )}
                                                    {/* <button
                                                    onClick={() => handleDeletePatient(patient.id, patient.name)}
                                                    className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                    <Trash2 className="h-4 w-4 relative z-10" />
                                                </button> */}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr data-testid="patient-no-data-row">
                                        <td colSpan={activeTab === 'active' ? "8" : "9"} data-testid="patient-no-data-text" className="p-3 text-center text-gray-500">
                                            {t('patientpage.no_patients_found')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Cards */}
                    <div data-testid="patient-mobile-cards" className="block sm:hidden space-y-4">
                        {currentPatients.length > 0 ? (
                            currentPatients.map((patient, index) => {
                                // Generate initials from name and surname
                                const getInitials = () => {
                                    const nameInitial = patient.firstName ? patient.firstName.charAt(0).toUpperCase() : '';
                                    const surnameInitial = patient.surname ? patient.surname.charAt(0).toUpperCase() : '';
                                    return nameInitial + surnameInitial;
                                };
                                const initials = getInitials();
                                return (
                                    <div key={patient.id} data-testid={`patient-mobile-card-${patient.patientId}`} className="p-4 bg-gradient-to-r bg-gray-50 rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                                        <div className="flex justify-between items-center mb-2">
                                            <span data-testid={`patient-mobile-serial-${patient.patientId}`} className="text-xs font-bold text-[#4ABDBD]">
                                                {t('patientpage.serial_no')}: {indexOfFirstPatient + index + 1}
                                            </span>
                                            <div className="flex gap-2">
                                                <button
                                                    data-testid={`patient-mobile-view-button-${patient.patientId}`}
                                                    onClick={() => navigate('/patientProfile', { state: { patientId: patient.id, returnTab: activeTab } })}
                                                    className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                    <Eye className="h-4 w-4 relative z-10" />
                                                </button>
                                                {userRole === 'admin' && (
                                                    <button
                                                        data-testid={`patient-mobile-password-button-${patient.patientId}`}
                                                        onClick={() => {
                                                            setSelectedPatientId(patient.id);
                                                            setIsPasswordModalOpen(true);
                                                        }}
                                                        className="group/btn text-purple-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                    >
                                                        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                        <KeyRound className="h-4 w-4 relative z-10" />
                                                    </button>
                                                )}
                                                {/* <button
                                                onClick={() => handleDeletePatient(patient.id, patient.name)}
                                                className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                <Trash2 className="h-4 w-4 relative z-10" />
                                            </button> */}
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-3 text-sm font-semibold text-gray-800">
                                                <div className="relative w-10 h-10 flex-shrink-0">
                                                    {patient.image ? (
                                                        <img
                                                            src={getFileURL(patient.image)}
                                                            alt={patient.name || 'Patient'}
                                                            className="w-10 h-10 rounded-full object-cover shadow-md"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                const fallback = e.target.parentElement.querySelector('.image-fallback');
                                                                if (fallback) fallback.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div
                                                        className={`image-fallback ${patient.image ? 'hidden' : 'flex'} absolute inset-0 w-10 h-10 rounded-full bg-[#4ABDBD] items-center justify-center shadow-md`}
                                                    >
                                                        {initials ? (
                                                            <span className="text-white text-sm font-bold uppercase">
                                                                {initials}
                                                            </span>
                                                        ) : (
                                                            <User className="w-5 h-5 text-white" />
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <span className="font-bold">{t('patientpage.full_name')}:</span> {patient.name || '-'}
                                                </div>
                                            </div>
                                            <div className="text-xs text-gray-600">
                                                <span className="font-bold">{t('patientpage.email')}:</span> {patient.email}
                                            </div>
                                            <div className="text-xs text-gray-600">
                                                <span className="font-bold">{t('patientpage.phone')}:</span> {patient.phone}
                                            </div>
                                            <div className="text-xs text-gray-600">
                                                <span className="font-bold">{t('patientpage.dob')}:</span> {formatDob(patient.dob)}
                                            </div>
                                            <div className="text-xs">
                                                <span className="font-bold">{t('patientpage.gender')}:</span>
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 text-[#3AA8A8] border border-[#4ABDBD]/20 ml-1">
                                                    {t(`patientpage.${patient.gender}`)}
                                                </span>
                                            </div>
                                            <div className="text-xs flex items-center gap-2">
                                                <span className="font-bold">{t('patientpage.status_label')}:</span>
                                                <div className="relative">
                                                    <div className={`relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full transition-colors duration-300 ${patient.status === 'active'
                                                        ? 'bg-[#4ABDBD]'
                                                        : 'bg-gray-300'
                                                        } ${(isLoading || patientsLoading) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                        onClick={() => !(isLoading || patientsLoading) && handleStatusToggle(patient)}
                                                    >
                                                        <span className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${patient.status === 'active'
                                                            ? 'translate-x-[24px] sm:translate-x-[24px]'
                                                            : 'translate-x-1'
                                                            }`}></span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div data-testid="patient-mobile-no-data" className="p-3 text-center text-gray-500">
                                {t('patientpage.no_patients_found')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Pagination */}
            {!isLoading && !patientsLoading && totalItems > 0 && (
                <Pagination
                    totalItems={totalItems}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    custombutton={currentPatients.length === 0 ? 'disable' : ''}
                />
            )}

            {/* Add Patient Modal */}
            <AddPatient isOpen={isModalOpen} onClose={handleCloseModal} onSubmit={handleAddPatient} />
            <BulkUploadPatients
                isOpen={isBulkModalOpen}
                onClose={() => setIsBulkModalOpen(false)}
                doctorId={doctorId}
                onComplete={() => setRefreshKey((k) => k + 1)}
            />

            {/* Password Update Modal */}
            <PasswordUpdateModal
                isOpen={isPasswordModalOpen}
                onClose={() => {
                    setIsPasswordModalOpen(false);
                    setSelectedPatientId(null);
                }}
                userId={selectedPatientId}
                userType="patient"
            />
        </div>
    );
}